// src/components/padrekko/qc/SendBackModal.jsx
//
// Phase A reverse-stage modal. Triggered from the "Send back" button on
// CaseBanner. Captures the required reason text + optional finding dispute
// flags, then calls useQcStore.reverseStage.
//
// Authorization (visibility of the parent button) is enforced upstream in
// CaseBanner — this modal trusts that the user reaching it is allowed to
// reverse. Backend re-validates on submission as defense in depth.
//
// The reason shell (overlay, textarea, error, buttons) comes from the shared
// MandatoryReasonModal; this component adds the dispute-finding checkbox list
// as children and owns the disputedIds selection state.

import { useState } from "react";
import { RotateCcw } from "lucide-react";
import {
  useQcStore,
  previousStageFor,
} from "../../../stores/useQcStore";
import MandatoryReasonModal from "./MandatoryReasonModal";

const STAGE_LABELS = {
  dh_review:       "DH Review",
  customer_review: "Customer Review",
  operator_review: "Operator Review",
  complete:        "Complete",
};

export default function SendBackModal({ isOpen, onClose }) {
  const caseInfo             = useQcStore((s) => s.caseInfo);
  const session              = useQcStore((s) => s.session);
  const reverseStage         = useQcStore((s) => s.reverseStage);
  const reverseStageInflight = useQcStore((s) => s.reverseStageInflight);

  const [disputedIds, setDisputedIds] = useState(() => new Set());

  if (!isOpen || !caseInfo) return null;

  const currentStage = caseInfo.current_stage;
  const previousStage = previousStageFor(currentStage);
  if (!previousStage) {
    // Shouldn't happen — CaseBanner gates on previousStage existence.
    return null;
  }

  // Collect findings across all pads, excluding ones already disputed
  // (can't double-flag) and any with no analysis (shouldn't display).
  const findings = (session?.pads ?? []).flatMap((pad) =>
    (pad.findings ?? [])
      .filter((f) => !f.is_disputed && f.analysis)
      .map((f) => ({
        id: f.id,
        idx: f.idx,
        pad_name: pad.pad_name,
        feature: f.analysis?.feature || f.analysis?.feature_label || "(unnamed)",
        status: f.analysis?.status || "VERIFY",
      }))
  );

  function toggleFinding(id) {
    setDisputedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Reset the dispute selection whenever the modal closes (cancel or success).
  function handleClose() {
    setDisputedIds(new Set());
    onClose();
  }

  return (
    <MandatoryReasonModal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Send case back to ${STAGE_LABELS[previousStage] ?? previousStage}?`}
      icon={<RotateCcw size={12} />}
      label="Reason for sending back"
      placeholder="Explain why this case needs more work at the previous stage."
      submitLabel="Send back"
      submittingLabel="Sending…"
      onSubmit={(reason) =>
        reverseStage({
          toStage: previousStage,
          reason,
          disputedFindingIds: Array.from(disputedIds),
        })
      }
    >
      {findings.length > 0 && (
        <div className="space-y-1.5">
          <label className="text-[10px] font-mono uppercase tracking-widest text-fg-secondary">
            Flag specific findings as disputed? (optional)
          </label>
          <div className="rounded border border-slate-800 bg-slate-900/40
                          max-h-[180px] overflow-y-auto">
            {findings.map((f) => (
              <label
                key={f.id}
                className="flex items-start gap-2 px-2 py-1.5 cursor-pointer
                           hover:bg-slate-800/40 transition border-b
                           border-slate-800/60 last:border-b-0"
              >
                <input
                  type="checkbox"
                  checked={disputedIds.has(f.id)}
                  onChange={() => toggleFinding(f.id)}
                  disabled={reverseStageInflight}
                  className="accent-amber-400 mt-[2px]"
                />
                <span className="text-[10px] leading-snug flex-1 min-w-0">
                  <span className="text-slate-400 font-medium">{f.pad_name}</span>
                  <span className="text-fg-secondary mx-1">·</span>
                  <span className="text-slate-200">{f.feature}</span>
                  <span className={`ml-1.5 text-[8px] font-mono uppercase tracking-widest
                    ${f.status === "NON_COMPLIANT" ? "text-red-300"
                      : f.status === "VERIFY"      ? "text-amber-300"
                                                   : "text-emerald-300"}`}>
                    {f.status}
                  </span>
                </span>
              </label>
            ))}
          </div>
          {disputedIds.size > 0 && (
            <div className="text-[9px] text-fg-secondary italic">
              {disputedIds.size} finding{disputedIds.size === 1 ? "" : "s"} will be flagged.
            </div>
          )}
        </div>
      )}
    </MandatoryReasonModal>
  );
}
